import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router';
import { ApolloClient, useQuery } from '@apollo/client';
import { withApollo } from '@apollo/client/react/hoc';
import { logException } from 'App/logging';
import { useUnmindTalk } from 'Talk/hooks';
import useFeatureFlag, { FEATURE_FLAGS } from 'flags/useFeatureFlag';
import gql from 'graphql-tag';
import { LoginAction } from '../LoginAction';
import RoutePath from '../../RoutePath';
import { tracking } from '../../Tracking';
import getSubdomainFromUrl from '../../../utils/getSubdomainFromUrl';
import LoadingIndicator from '../../../Shared/LoadingIndicator';
import { identifyBrazeUserAndMergeProfiles } from '../../../App/braze/identifyBrazeUser';
import useFeatureFlagUserContext from '../../../flags/useFeatureFlagUserContext';
import useIsFirstLoginClaim from './useIsFirstLoginClaim';
import { getUserNovaEntitlement } from './__generated__/getUserNovaEntitlement';

export interface SSOLoginSuccessProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client?: ApolloClient<any>;
}

export const NOVA_ENTITLEMENT_QUERY = gql`
  query getUserNovaEntitlement {
    user {
      novaEntitlement {
        value
      }
      departmentData {
        id
      }
      locationData {
        id
      }
    }
  }
`;

const SSOLoginSuccess = (props: SSOLoginSuccessProps) => {
  /**
   * Component that loads after successful authentication via Auth0 SSO
   * used to handle any required post-authenticated actions before
   * redirect to target location
   */
  const [featureFlagsDone, setFeatureFlagsDone] = useState(false);
  const { identifyFeatureFlagUser } = useFeatureFlagUserContext();
  const {
    loading: loadingIsFirstLoginClaim,
    isFirstLogin: isFirstLoginClaim,
    unmindUserId: userIdClaim,
  } = useIsFirstLoginClaim();
  const { unmindTalkIsAvailableForUser, loading: loadingTalk } =
    useUnmindTalk();
  const { data: userData, loading: userLoading } =
    useQuery<getUserNovaEntitlement>(NOVA_ENTITLEMENT_QUERY, {
      fetchPolicy: 'network-only',
    });
  const showTalkOnboardingModal = useFeatureFlag(
    FEATURE_FLAGS.SHOW_TALK_ONBOARDING_MODAL,
  );

  const subdomain = getSubdomainFromUrl();

  useEffect(() => {
    if (userIdClaim && identifyFeatureFlagUser && !userLoading) {
      const value = userData?.user?.novaEntitlement?.value;
      const novaEntitlement =
        value === '1' ? true : value === '0' ? false : undefined;
      const identify = async () => {
        await identifyFeatureFlagUser({
          userId: userIdClaim,
          subdomain,
          novaEntitlement,
          locationId: userData?.user?.locationData?.id || undefined,
          departmentId: userData?.user?.departmentData?.id || undefined,
        });
        setFeatureFlagsDone(true);
      };

      void identify();
    }
  }, [userIdClaim, identifyFeatureFlagUser, userData, userLoading, subdomain]);

  useEffect(() => {
    if (userIdClaim) {
      if (props.client) {
        void identifyBrazeUserAndMergeProfiles(props.client, userIdClaim);
      } else {
        logException(
          'attempted to identify braze user but apollo client not initialised',
        );
      }
    }
  }, [props.client, userIdClaim]);

  if (
    !featureFlagsDone ||
    loadingIsFirstLoginClaim ||
    loadingTalk ||
    userLoading
  ) {
    return <LoadingIndicator />;
  }

  tracking.track('login-successful', {
    subdomain,
    loginAction: LoginAction.SSO_LOGIN,
    isFirstLogin: isFirstLoginClaim,
    type: 'success',
  });

  if (isFirstLoginClaim) {
    if (showTalkOnboardingModal && unmindTalkIsAvailableForUser) {
      return (
        <Redirect
          to={{
            pathname: RoutePath.Home,
            state: { isFirstLogin: true },
          }}
        />
      );
    } else {
      return <Redirect to={RoutePath.Home} />;
    }
  }

  const urlSearchParams = new URLSearchParams(location.search);
  const returnTo = urlSearchParams.get('returnto');

  if (returnTo) {
    return <Redirect to={returnTo} />;
  }

  return <Redirect to={RoutePath.Home} />;
};

export default withApollo<SSOLoginSuccessProps>(SSOLoginSuccess);
